import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { ListingCard } from "@/components/site/ListingCard";
import {
  getListingByIdSlug,
  getCategory,
  getLocation,
  relatedListings,
  nearbyListings,
  type Review,
} from "@/lib/data";
import { Star, MapPin, Phone, Globe, Clock, Check, Share2, Heart } from "lucide-react";
import { generateBreadcrumbSchema } from "@/lib/utils";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/listing/$idSlug")({
  loader: ({ params }) => {
    const listing = getListingByIdSlug(params.idSlug);
    if (!listing) throw notFound();
    const category = getCategory(listing.category)!;
    const location = getLocation(listing.location)!;
    return { listing, category, location };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { listing, category, location } = loaderData;
    const title = `${listing.name} — ${category.name} in ${location.name} | LetsListBusiness`;
    const desc = `${listing.name} in ${location.name}, ${location.state}. ${listing.rating}★ from ${listing.reviews} reviews. ${listing.description.slice(0, 100)}…`;
    const url = `${SITE_URL}/listing/${listing.id}-${listing.slug}`;
    
    const ld = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: listing.name,
      image: listing.image,
      address: {
        "@type": "PostalAddress",
        streetAddress: listing.address,
        addressLocality: location.name,
        addressRegion: location.state,
      },
      telephone: listing.phone,
      priceRange: listing.priceRange,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: listing.rating,
        reviewCount: listing.reviews,
      },
      url: url,
    };
    
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:image", content: listing.image },
        { property: "og:type", content: "business.business" },
        { property: "og:url", content: url },
        { name: "twitter:image", content: listing.image },
      ],
      links: [
        { rel: "canonical", href: url },
      ],
      schema: [
        ld,
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: location.name, href: `/location/${location.slug}/` },
          { label: category.name, href: `/location/${location.slug}/${category.slug}` },
          { label: listing.name, href: url },
        ], SITE_URL),
      ],
    };
  },
  component: ListingPage,
  notFoundComponent: () => (
    <div className="container mx-auto px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Listing not found</h1>
      <Link to="/" className="mt-4 inline-block text-primary">Back home</Link>
    </div>
  ),
});

function ListingPage() {
  const { listing, category, location } = Route.useLoaderData();
  const related = relatedListings(listing, 3);
  const nearby = nearbyListings(listing, 3);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: location.name, href: `/location/${location.slug}/` },
          { label: category.name, href: `/location/${location.slug}/${category.slug}` },
          { label: listing.name },
        ]}
      />

      {/* Gallery hero — main + 2 stack */}
      <section className="container mx-auto px-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="overflow-hidden rounded-2xl sm:col-span-2">
            <img src={listing.image} alt={listing.name} className="aspect-16/10 w-full object-cover" />
          </div>
          <div className="grid gap-3">
            <div className="overflow-hidden rounded-2xl">
              <img src={category.image} alt={`${category.name} in ${location.name}`} className="aspect-4/3 w-full object-cover sm:aspect-auto sm:h-full" />
            </div>
            <div className="hidden overflow-hidden rounded-2xl sm:block">
              <img src={location.image} alt={`${location.name}, ${location.state} cityscape`} className="h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Title block — overlapping floating card */}
      <section className="container mx-auto px-4">
        <div className="relative -mt-10 rounded-3xl border border-border bg-card p-6 shadow-card-lg sm:-mt-16 sm:p-10">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Link to="/category/$slug" params={{ slug: category.slug }} className="rounded-full bg-primary/10 px-3 py-1 font-semibold uppercase tracking-wider text-primary">
                  {category.icon} {category.name}
                </Link>
                <Link to="/location/$slug" params={{ slug: location.slug }} className="rounded-full bg-secondary/10 px-3 py-1 font-semibold uppercase tracking-wider text-secondary">
                  <MapPin className="mr-1 inline h-3 w-3" />
                  {location.name}, {location.state}
                </Link>
                <span className="rounded-full border border-border px-3 py-1 font-mono text-xs text-muted-foreground">{listing.priceRange}</span>
              </div>
              <h1 className="mt-4 font-display text-4xl font-bold leading-tight sm:text-6xl">{listing.name}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
                <span className="flex items-center gap-1.5 rounded-full bg-foreground px-3 py-1.5 font-semibold text-background">
                  <Star className="h-3.5 w-3.5 fill-accent stroke-accent" /> {listing.rating}
                </span>
                <span className="text-muted-foreground">{listing.reviews} reviews</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-muted-foreground">{listing.hours}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="grid h-11 w-11 place-items-center rounded-full border border-border hover:bg-muted" aria-label="Save">
                <Heart className="h-4 w-4" />
              </button>
              <button className="grid h-11 w-11 place-items-center rounded-full border border-border hover:bg-muted" aria-label="Share">
                <Share2 className="h-4 w-4" />
              </button>
              <button className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-95">
                Write a review
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Body — content + sidebar */}
      <section className="container mx-auto grid gap-10 px-4 py-12 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-primary">About</h2>
          <p className="mt-3 text-lg leading-relaxed text-foreground/85">{listing.description}</p>

          <h2 className="mt-12 font-display text-sm font-semibold uppercase tracking-[0.2em] text-primary">What people love</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {listing.features.map((f: string) => (
              <li key={f} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 text-sm">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-success/15 text-success">
                  <Check className="h-3.5 w-3.5" />
                </span>
                {f}
              </li>
            ))}
          </ul>

          <h2 className="mt-12 font-display text-sm font-semibold uppercase tracking-[0.2em] text-primary">Recent reviews</h2>
          <div className="mt-4 space-y-4">
            {listing.recentReviews.map((r: Review, i: number) => (
              <article key={i} className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="grid h-11 w-11 place-items-center rounded-full bg-gradient-warm font-display text-lg font-bold text-accent-foreground">
                      {r.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-medium">{r.name}</div>
                      <div className="text-xs text-muted-foreground">{r.date}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: r.stars }).map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-accent stroke-accent" />
                    ))}
                  </div>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{r.text}</p>
              </article>
            ))}
          </div>
        </div>

        {/* Sidebar — sticky info panel */}
        <aside>
          <div className="sticky top-24 space-y-4">
            <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
              <div className="bg-gradient-hero p-5 text-white">
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/80">Contact & info</div>
                <div className="mt-2 font-display text-2xl font-bold">{listing.name}</div>
              </div>
              <ul className="space-y-4 p-5 text-sm">
                <InfoRow icon={MapPin} label="Address">{listing.address}</InfoRow>
                <InfoRow icon={Phone} label="Phone">{listing.phone}</InfoRow>
                <InfoRow icon={Globe} label="Website">{listing.website}</InfoRow>
                <InfoRow icon={Clock} label="Hours">{listing.hours}</InfoRow>
              </ul>
              <div className="border-t border-border p-5">
                <button className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground">
                  Call now
                </button>
                <button className="mt-2 w-full rounded-full border border-border py-3 text-sm font-semibold hover:bg-muted">
                  Get directions
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-surface p-5">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Quick links</div>
              <ul className="mt-3 space-y-2 text-sm">
                <li><Link to="/category/$slug" params={{ slug: category.slug }} className="hover:text-primary">All {category.name.toLowerCase()}</Link></li>
                <li><Link to="/location/$slug" params={{ slug: location.slug }} className="hover:text-primary">More in {location.name}</Link></li>
                <li><Link to="/location/$loc/$cat" params={{ loc: location.slug, cat: category.slug }} className="hover:text-primary">{category.name} in {location.name}</Link></li>
              </ul>
            </div>
          </div>
        </aside>
      </section>

      {/* Related */}
      <section className="container mx-auto px-4 pb-12">
        <h2 className="font-display text-2xl font-bold">Related {category.name.toLowerCase()}</h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      </section>

      {/* Nearby */}
      <section className="bg-surface py-12">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-2xl font-bold">Nearby in {location.name}</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {nearby.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        </div>
      </section>
    </>
  );
}

function InfoRow({ icon: Icon, label, children }: { icon: React.ComponentType<{ className?: string }>; label: string; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-0.5 wrap-break-word">{children}</div>
      </div>
    </li>
  );
}
